import { Link } from "react-router";
import { getTranslationFn, type Locale } from "../i18n";

export function ErrorPage({
  is404,
  stack,
  locale,
}: {
  is404: boolean;
  stack?: string;
  locale: Locale;
}) {
  const t = getTranslationFn(locale);
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-4 font-sans">
      <div className="relative flex flex-col items-center text-center">
        {/* Large faded background number */}
        <span className="pointer-events-none select-none text-[10rem] font-bold leading-none tracking-tighter text-primary/5 sm:text-[14rem]">
          {is404 ? "404" : "Oops"}
        </span>

        {/* Content overlaid on the number */}
        <div className="-mt-16 flex flex-col items-center gap-3 sm:-mt-20">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            {is404 ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
                <path d="m9 9 4 4" />
                <path d="m13 9-4 4" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
            )}
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {is404 ? t("Lost in the legal maze?") : t("Something went wrong")}
          </h1>

          <p className="max-w-md text-muted-foreground">
            {is404
              ? t(
                  "The page you're looking for doesn't exist or has been moved."
                )
              : t("An unexpected error occurred. Please try again.")}
          </p>

          <div className="mt-4 flex gap-3">
            <Link
              to="/"
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {t("Go back to main page")}
            </Link>
          </div>

          {/* Dev stack trace */}
          {stack && (
            <pre className="mt-8 max-w-2xl overflow-auto rounded-lg bg-muted p-4 text-left text-xs text-muted-foreground">
              <code>{stack}</code>
            </pre>
          )}
        </div>
      </div>
    </main>
  );
}
